import { request } from './client';
import type { ApiResult } from './errors';
import type {
  AccountProfile,
  AchievementOverview,
  AnswerOutcome,
  Authentication,
  ChallengeAnswer,
  Child,
  ChildDashboard,
  ChildDraft,
  ChildEdit,
  ErrorPattern,
  FamilyOverview,
  Hint,
  Island,
  OauthProvider,
  Registration,
  SkillDetail,
  Today,
  TokenPair,
  TrainingSession,
} from './models';

/**
 * Ported from `MysliKrokApi.kt`, method for method and path for path.
 * A change to the Kotlin changes this file in the same commit — client code manifest §2a.
 */
export const Api = {
  // ── Authentication ────────────────────────────────────────────────────────────

  /** Which sign-in providers this deployment offers. The buttons are rendered from this, never guessed. */
  providers: (): Promise<ApiResult<OauthProvider[]>> => request('/auth/providers', 'GET', undefined, false),

  signIn: (email: string, password: string): Promise<ApiResult<Authentication>> =>
    request('/auth/login', 'POST', { email, password }, false),

  /**
   * Registers a parent. Returns **nothing**, and answers identically whether or not the address is already
   * taken — that is the point of it (R0031). The account exists afterwards either way from the caller's
   * perspective; only the mail says which happened, and only its owner reads that.
   */
  register: (registration: Registration): Promise<ApiResult<void>> =>
    request('/auth/register', 'POST', registration, false),

  requestPasswordReset: (email: string): Promise<ApiResult<void>> =>
    request('/auth/forgot-password', 'POST', { email }, false),

  resetPassword: (token: string, password: string): Promise<ApiResult<void>> =>
    request('/auth/reset-password', 'POST', { token, password }, false),

  signOut: (): Promise<ApiResult<void>> => request('/auth/logout', 'POST'),

  readMe: (): Promise<ApiResult<AccountProfile>> => request('/auth/me', 'GET'),

  /** Exchanges the parent's token for a child-scoped one. The parent's token is untouched (ADR-0006). */
  issueChildToken: (childId: string): Promise<ApiResult<TokenPair>> =>
    request(`/auth/children/${childId}/token`, 'POST'),

  // ── Children ──────────────────────────────────────────────────────────────────

  listChildren: (): Promise<ApiResult<Child[]>> => request('/children', 'GET'),

  readFamilyOverview: (): Promise<ApiResult<FamilyOverview>> => request('/insights/overview', 'GET'),

  createChild: (draft: ChildDraft): Promise<ApiResult<Child>> => request('/children', 'POST', draft),

  updateChild: (childId: string, edit: ChildEdit): Promise<ApiResult<Child>> =>
    request(`/children/${childId}`, 'PATCH', edit),

  archiveChild: (childId: string): Promise<ApiResult<Child>> =>
    request(`/children/${childId}/archive`, 'POST'),

  restoreChild: (childId: string): Promise<ApiResult<Child>> =>
    request(`/children/${childId}/restore`, 'POST'),

  // ── Training ──────────────────────────────────────────────────────────────────

  startDailyTraining: (): Promise<ApiResult<TrainingSession>> => request('/training/daily', 'POST'),

  readSession: (sessionId: string): Promise<ApiResult<TrainingSession>> =>
    request(`/training/sessions/${sessionId}`, 'GET'),

  submitAnswer: (answer: ChallengeAnswer): Promise<ApiResult<AnswerOutcome>> =>
    request('/training/answers', 'POST', answer),

  // ── Insights — parent only; a child token is refused by the API ───────────────

  readDashboard: (childId: string): Promise<ApiResult<ChildDashboard>> =>
    request(`/insights/children/${childId}/dashboard`, 'GET'),

  readSkillDetail: (childId: string, skillCode: string): Promise<ApiResult<SkillDetail>> =>
    request(`/insights/children/${childId}/skills/${skillCode}`, 'GET'),

  readErrorPatterns: (childId: string): Promise<ApiResult<ErrorPattern[]>> =>
    request(`/insights/children/${childId}/error-patterns`, 'GET'),

  // ── Rewards — child mode ──────────────────────────────────────────────────────

  readIsland: (): Promise<ApiResult<Island>> => request('/rewards/island', 'GET'),

  /** Today's training without starting one — `POST /training/daily` creates a session as a side effect. */
  readToday: (): Promise<ApiResult<Today>> => request('/training/today', 'GET'),

  /** Confirm an address from a link. Public — the parent may not be signed in on the device they open it. */
  verifyEmail: (token: string): Promise<ApiResult<void>> => request('/auth/verify-email', 'POST', { token }),

  resendVerification: (): Promise<ApiResult<void>> => request('/auth/resend-verification', 'POST', {}),

  /** Correct the address, while it is still unverified. */
  changeEmail: (email: string): Promise<ApiResult<AccountProfile>> =>
    request('/auth/email', 'PATCH', { email }),

  /** A hint on request. Counts towards `hintsUsed`, which is why the server hands them out one at a time. */
  requestHint: (instanceToken: string): Promise<ApiResult<Hint>> =>
    request('/training/hints', 'POST', { instanceToken }),

  unlockIslandPart: (partCode: string): Promise<ApiResult<Island>> =>
    request(`/rewards/island/${partCode}/unlock`, 'POST'),

  // ── Achievements ──────────────────────────────────────────────────────────────

  /** The child reading their own. Child-scoped: a parent token is refused. */
  readAchievements: (): Promise<ApiResult<AchievementOverview>> => request('/achievements', 'GET'),

  /** A parent reading their own child's. The ownership check is what makes it a separate endpoint. */
  readChildAchievements: (childId: string): Promise<ApiResult<AchievementOverview>> =>
    request(`/insights/children/${childId}/achievements`, 'GET'),
};
